import React from 'react';
import { Controller } from 'react-hook-form';
import type {
  Control,
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import {
  FormSelect,
  VirtualizedFormSelect,
  CollapsibleSection,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import { Field, DateField } from '../../FormFields';
import { formatPhone, formatSSN, formatZipCode } from '../../../utils/intake';
import { LANGUAGE_OPTIONS } from '../../../hooks/useCreatePatientForm';
import { GENDER_OPTIONS } from '../../../constants/options';
import type { CreateEpisodeStepFormData } from '../../../schemas/createEpisodeStepFormSchema';

interface PatientDetailsSectionProps {
  // Presentation
  /** Optional override for section title (defaults to "Patient Details"). */
  title?: string;
  /**
   * When true, section is used for search-only flows.
   * Only first/last name are treated as required; all other fields are optional.
   */
  searchMode?: boolean;

  // Form controls
  control: Control<CreateEpisodeStepFormData>;
  register: UseFormRegister<CreateEpisodeStepFormData>;
  errors: FieldErrors<CreateEpisodeStepFormData>;
  setValue: UseFormSetValue<CreateEpisodeStepFormData>;
  handleFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;
  saveToServer: (data: Partial<CreateEpisodeStepFormData>) => void;

  // Address validation
  isValidatingAddress: boolean;
  handleAddressFieldBlur: (
    fieldNameOrEvent: React.FocusEvent<HTMLElement>
  ) => void;

  // Language key for conditional rendering
  kinnserLanguageKey: string | null | undefined;

  // Whether we are creating for Roger EMR
  pushToRoger?: boolean;
}

export const PatientDetailsSection: React.FC<PatientDetailsSectionProps> = ({
  title = 'Patient Details',
  searchMode = false,
  control,
  register,
  errors,
  handleFieldBlur,
  saveToServer,
  isValidatingAddress,
  handleAddressFieldBlur,
  kinnserLanguageKey,
  pushToRoger,
}) => {
  return (
    <CollapsibleSection title={title}>
      <div className='grid grid-cols-4 gap-3'>
        {/* Row 1: Name */}
        <Field
          label='First Name'
          htmlFor='patient_first_name'
          required
          error={errors.patient_first_name?.message}
          inputProps={register('patient_first_name', {
            onBlur: handleFieldBlur,
          })}
        />
        <Field
          label='Middle Name'
          htmlFor='patient_middle_name'
          inputProps={register('patient_middle_name', {
            onBlur: handleFieldBlur,
          })}
        />
        <Field
          label='Last Name'
          htmlFor='patient_last_name'
          required
          error={errors.patient_last_name?.message}
          inputProps={register('patient_last_name', {
            onBlur: handleFieldBlur,
          })}
        />
        <Field
          label='Suffix'
          htmlFor='suffix'
          inputProps={register('patient_suffix', {
            onBlur: handleFieldBlur,
          })}
        />

        {/* Row 2: Key identifiers */}
        <Controller
          name='patient_dob'
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <DateField
              label='Date of Birth'
              value={value}
              htmlFor='dob'
              required={!searchMode}
              warning={!value}
              onChange={val => {
                onChange(val);
                saveToServer({ patient_dob: val });
              }}
              onBlur={onBlur}
              yearRange={{
                fromYear: new Date().getFullYear() - 120,
                toYear: new Date().getFullYear(),
              }}
            />
          )}
        />
        <FormSelect
          label='Gender'
          placeholder='Select gender...'
          options={GENDER_OPTIONS}
          required={!searchMode}
          error={errors.patient_gender?.message}
          {...register('patient_gender', {
            onBlur: handleFieldBlur,
          })}
        />

        <Controller
          name='patient_ssn'
          control={control}
          render={({ field }) => (
            <Field
              label='SSN'
              htmlFor='ssn'
              error={errors.patient_ssn?.message}
              maintainCursorPosition
              inputProps={{
                ...field,
                value: field.value || '',
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatSSN(e.target.value);
                  field.onChange(formatted);
                },
                onBlur: handleFieldBlur,
              }}
            />
          )}
        />
        <Controller
          name='patient_phone'
          control={control}
          render={({ field }) => (
            <Field
              label='Phone'
              htmlFor='phone'
              required={!searchMode}
              error={errors.patient_phone?.message}
              maintainCursorPosition
              inputProps={{
                ...field,
                value: field.value || '',
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatPhone(e.target.value);
                  field.onChange(formatted);
                },
                onBlur: handleFieldBlur,
              }}
            />
          )}
        />
        <Controller
          name='patient_secondary_phone'
          control={control}
          render={({ field }) => (
            <Field
              label='Secondary / Backup Phone'
              htmlFor='patient_secondary_phone'
              error={errors.patient_secondary_phone?.message}
              maintainCursorPosition
              inputProps={{
                ...field,
                value: field.value || '',
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatPhone(e.target.value);
                  field.onChange(formatted);
                },
                onBlur: handleFieldBlur,
              }}
            />
          )}
        />
        {/* Row 3: Address */}
        <div className='col-span-2'>
          <Field
            label='Street Address'
            htmlFor='street'
            required={!searchMode}
            error={errors.patient_street_address?.message}
            inputProps={register('patient_street_address', {
              onBlur: handleAddressFieldBlur,
            })}
            extra={
              isValidatingAddress && (
                <div className='text-xs flex items-center gap-1.5 text-primary'>
                  <Loader2 className='h-3 w-3 animate-spin' />
                  Validating...
                </div>
              )
            }
          />
        </div>
        <Field
          label='City'
          htmlFor='city'
          required={!searchMode}
          error={errors.patient_city?.message}
          inputProps={register('patient_city', {
            onBlur: handleFieldBlur,
          })}
        />
        <Field
          label='State'
          htmlFor='state'
          required={!searchMode}
          error={errors.patient_state?.message}
          inputProps={register('patient_state', {
            onBlur: handleFieldBlur,
          })}
        />
        {/* Row 4: Additional details */}
        <Controller
          name='patient_zip_code'
          control={control}
          render={({ field }) => (
            <Field
              label='Zip Code'
              htmlFor='zip'
              required={!searchMode}
              error={errors.patient_zip_code?.message}
              maintainCursorPosition
              inputProps={{
                ...field,
                value: field.value || '',
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatZipCode(e.target.value);
                  field.onChange(formatted);
                },
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  field.onBlur();
                  handleAddressFieldBlur(e);
                },
              }}
            />
          )}
        />
        <Field
          label='County'
          htmlFor='county'
          inputProps={register('patient_county', {
            onBlur: handleFieldBlur,
          })}
        />
        <Field
          label='Email'
          htmlFor='email'
          error={errors.patient_email?.message}
          inputProps={register('patient_email', {
            onBlur: handleFieldBlur,
          })}
        />
        {!pushToRoger && (
          <>
            <VirtualizedFormSelect
              label='Language'
              placeholder='Select language'
              options={LANGUAGE_OPTIONS}
              required={!searchMode}
              error={errors.kinnser_language_key?.message}
              {...register('kinnser_language_key', {
                onBlur: handleFieldBlur,
              })}
            />
            {kinnserLanguageKey === '6' && (
              <Field
                label='Other Language'
                htmlFor='kinnser_other_language'
                required
                error={errors.kinnser_other_language?.message}
                inputProps={register('kinnser_other_language', {
                  onBlur: handleFieldBlur,
                })}
              />
            )}
          </>
        )}
      </div>
    </CollapsibleSection>
  );
};
